import '../SettingsPage.scss';
import { Button, Checkbox, Icon, Input } from '@design-system/index';
import { ArchiveFilled, ArchiveLine, ModifierLine, ModifierFull, UnarchiveLine, UnarchiveFilled } from '@icons';
import { MutationTable } from '@components/Table/MutationTable/MutationTable';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { useNavigate } from 'react-router-dom';
import { useTicketTypesPageVM } from './useTicketTypesPageVM';
import { Formik } from 'formik';
import { TicketType, TicketTypeToCreate } from '@services/member/list/ticket-types/ticket-types.types';
import { formatMoney } from '@utils/numbers';

export default function TicketTypesPage() {
    const {
        mutationHook,
        isArchived,
        handleMetadata,
        handleArchiveTicketTypes,
        updateTicketType,
        createTicketType,
        MySwal,
        needReload,
        setIsArchived
    } = useTicketTypesPageVM();
    const columnHelper = createColumnHelper<TicketType>();
    const navigate = useNavigate();

    const handleModificationTicketTypes = async (ticketType: TicketTypeToCreate | null, id?: number) => {
        await MySwal.fire<TicketTypeToCreate>({
            title: ticketType ? "Modification d'un type de billet" : "Création d'un type de billet",
            showCancelButton: true,
            confirmButtonColor: '#01539D',
            confirmButtonText: ticketType ? 'Modifier' : 'Créer',
            cancelButtonColor: '#FF5F46',
            cancelButtonText: 'Annuler',
            html: (
                <Formik
                    initialValues={{ amount: ticketType?.amount ?? '', label: ticketType?.label ?? '' }}
                    onSubmit={(values) => {
                        if (id) {
                            updateTicketType({
                                id: id,
                                amount: Number(values.amount),
                                label: values.label
                            });
                        } else {
                            createTicketType({
                                amount: Number(values.amount),
                                label: values.label
                            });
                        }
                        MySwal.close();
                    }}
                >
                    {({ values, handleChange, handleSubmit }) => (
                        <form id="form-ticket-type" onSubmit={handleSubmit}>
                            <Input name="label" label="Libellé" required placeholder="Libellé" value={values.label} onChange={handleChange} />
                            <Input name="amount" type="number" rightContent={'€'} required label="Montant" placeholder="Montant" value={values.amount} onChange={handleChange} />
                        </form>
                    )}
                </Formik>
            ),
            focusConfirm: false,
            preConfirm: () => {
                const form = document.getElementById('form-ticket-type') as HTMLFormElement;
                form.requestSubmit();
                return false;
            }
        });
    };

    const columns = [
        columnHelper.display({
            id: 'select',
            size: 0,
            cell: ({ row }) => (
                <Checkbox
                    checked={row.getIsSelected()}
                    onChange={row.getToggleSelectedHandler()}
                    aria-label={`Sélectionner ligne ${row.id}`}
                />
            )
        }),
        columnHelper.accessor('label', {
            header: 'Libellé',
            enableSorting: true,
            cell: (info) => {
                return <>{info.getValue()}</>;
            }
        }),
        columnHelper.accessor('amount', {
            header: 'Montant',
            enableSorting: true,
            cell: (info) => {
                return <>{formatMoney(info.getValue())}</>;
            }
        }),
        columnHelper.accessor('id', {
            header: 'Actions',
            cell: (info) => {
                return (
                    <div className="settings-actions">
                        <Button
                            variant="icon"
                            color="primary"
                            size="small"
                            hoverIcon={<Icon icon={<ModifierFull />} size={20} color="primary" />}
                            icon={<Icon icon={<ModifierLine />} size={20} color="primary" />}
                            onClick={() => {
                                handleModificationTicketTypes(
                                    { amount: info.row.original.amount, label: info.row.original.label },
                                    info.row.original.id
                                );
                            }}
                        />
                        <Button
                            variant="icon"
                            color="primary"
                            size="small"
                            hoverIcon={<Icon icon={isArchived ? <UnarchiveFilled /> : <ArchiveFilled />} size={20} color="primary" />}
                            icon={<Icon icon={isArchived ? <UnarchiveLine /> : <ArchiveLine />} size={20} color="primary" />}
                            onClick={() => {
                                handleArchiveTicketTypes([info.row.original], !isArchived);
                            }}
                        />
                    </div>
                );
            }
        })
    ] as ColumnDef<TicketType>[];

    return (
        <>
            <div className="settings-tabs-content service-codes">
                <MutationTable
                    noHeader
                    key={isArchived ? 'archived' : 'active'}
                    columns={columns}
                    mutationHook={mutationHook}
                    onRowClick={(row) => navigate(`/members-management/settings/ticket-types/${row.id}`)}
                    defaultSorting={{
                        field: 'label',
                        direction: 'desc'
                    }}
                    needReload={needReload}
                    onMetadata={handleMetadata}
                    createButtonOnClick={() => handleModificationTicketTypes(null)}
                    createButtonLabel="Créer un type de billet"
                    archiveButtonOnClick={() => setIsArchived(!isArchived)}
                    archiveButtonLabel={isArchived ? 'Actifs' : 'Archivés'}
                    deleting={{
                        enabled: isArchived,
                        entity: 'ticket_type',
                    }}
                    footer={{
                        actions: [
                            {
                                label: isArchived ? 'Désarchiver' : 'Archiver',
                                icon: isArchived ? <UnarchiveLine /> : <ArchiveLine />,
                                hoverIcon: isArchived ? <UnarchiveFilled /> : <ArchiveFilled />,
                                onClick: (lines: TicketType[]) => handleArchiveTicketTypes(lines, !isArchived)
                            }
                        ]
                    }}
                />
            </div>
        </>
    );
}
